'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useGSAP } from '@gsap/react';

const animationMobile = (ref: React.RefObject<HTMLDivElement | null>) => {
  const { current } = ref;
  if (!current) return;
  const tlTitle = gsap.timeline({
    scrollTrigger: {
      trigger: current.querySelector('.main-title'),
      start: 'top 80%',
      end: 'bottom 80%',
      toggleActions: 'play none none reverse',
    },
  });
  tlTitle
    .fromTo(
      current.querySelector('video'),
      {
        currentTime: 0,
      },
      {
        currentTime: 2,
      },
    )
    .fromTo(
      current.querySelector('.main-title'),
      {
        scale: 0,
      },
      {
        scale: 1,
        duration: 1,
      },
    );

  const box1 = current.querySelector('.box-1');
  const box2 = current.querySelector('.box-2');

  if (!box1 || !box2) return;

  const arrayBox = [box1, box2];

  arrayBox.forEach(box => {
    const tlBox = gsap.timeline({
      defaults: {
        duration: 1,
        ease: 'none',
      },
      scrollTrigger: {
        trigger: box,
        start: 'top 80%',
        end: 'bottom 80%',
        scrub: true,
      },
    });
    tlBox
      .from(box.querySelector('[data-title]'), {
        opacity: 0,
        y: 50,
      })
      .from(box.querySelector('.text-wrapper h2'), {
        opacity: 0,
        y: 50,
      })
      .from(box.querySelector('.text-wrapper .image-wrapper .image-1'), {
        opacity: 0,
        y: 50,
      })
      .from(
        box.querySelector('.text-wrapper .image-wrapper .image-2'),
        {
          opacity: 0,
          y: 50,
        },
        '-=0.5',
      )
      .from(box.querySelector('.text-wrapper h5'), {
        opacity: 0,
        y: 50,
      })

      .from(box.querySelector('.text-wrapper a'), {
        opacity: 0,
        y: 50,
      });
  });
};

export const ProactiveReactiveSectionMobile: React.FC = () => {
  const ref = React.useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);
      animationMobile(ref);
    },
    { scope: ref },
  );
  return (
    <div className="relative flex flex-col justify-center bg-black lg:hidden" ref={ref}>
      <div className="video laptop relative left-1/2 top-0 w-full -translate-x-1/2 transform object-cover will-change-transform">
        <video muted height={'100%'} width={'100%'} className="mix-blend-lighten">
          <source src="/assets/videos/LAPTOP-CROPPED(compressed).mp4" type="video/mp4" />
        </video>
        <div className=" main-title absolute left-1/2 top-[10vw] z-10 max-w-[300px] -translate-x-1/2 text-center ">
          <div className="textActiveSource">
            <p className="source-flip textActive text-h1-sm text-gradle-blue md:text-headline-md lg:text-splash-md">
              Active
            </p>
          </div>
          <h5 className="text-h4 text-light-background-200">build & test monitoring</h5>
        </div>
      </div>
      <div className="container">
        <div className="box-wrapper relative z-10 ">
          <div className={`box-1 mb-12 flex pb-8 text-left text-white`}>
            <div className="flex p-0">
              <div className="w-full pr-0 md:w-full md:pr-0 lg:w-1/2 lg:pr-14">
                <div
                  data-title
                  className="mb-4 flex justify-center text-center text-h1-sm text-white md:text-headline-md lg:justify-normal lg:text-left lg:text-splash-md"
                >
                  <span className="pro inline-flex w-[86px] md:w-[130px] lg:w-[160px]">
                    Pro
                    <span className="source-flip textActive text-h1-sm text-gradle-blue md:text-headline-md lg:text-splash-md">
                      Actively
                    </span>
                  </span>
                  <div className="inline-flex text-gradle-blue ">
                    <p className="textActiveTarget inline-flex w-[155px] md:w-[230px] lg:w-[290px]"> </p>
                    <p className="ly hidden lg:inline-flex">ly</p>
                  </div>
                </div>
                <div className="text-wrapper">
                  <h2 className="text-h4 text-center text-gray-5 lg:text-left">address avoidable failures</h2>
                  <div className="image-wrapper relative mb-20 mt-10 flex flex-col lg:mb-0 lg:mt-0 ">
                    <Image
                      src="/assets/images/active/SCREEN-PROACTIVE-FNL.png"
                      alt="reactive 1"
                      width={800}
                      height={500}
                      className="image-1 w-full px-6"
                    />
                    <Image
                      src="/assets/images/active/Proactively 02.png"
                      alt="reactive 1"
                      width={250}
                      height={150}
                      className=" image-2 absolute bottom-[-10%] right-[10px]"
                    />
                  </div>
                  <h5 className="mb-6 text-body-lg text-gray-4 md:text-eyebrow-lg lg:text-h5-sm">
                    Find unreliable builds and tests, like flaky tests, learn how many people and environments are
                    affected, share information about them, and understand the root cause efficiently.
                  </h5>
                  <Link
                    href="/develocity/product/failure-analytics/"
                    className="underline-text-transition-blue group !flex !w-fit items-center gap-1"
                  >
                    <span className="text-body">Learn More</span>
                    <ChevronRightIcon className="size-6 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className={`box-2 relative left-0 top-0 flex p-0 text-left text-white md:p-10`}>
            <div className="flex p-0">
              <div className="w-full pr-0 md:w-full md:pr-0 lg:w-1/2 lg:pr-14">
                <div
                  data-title
                  className="mb-4 flex justify-center text-h1-sm text-white md:text-headline-md lg:justify-normal lg:text-splash-md"
                >
                  <span className="pro inline-flex w-[86px] md:w-[118px] lg:w-[160px]">
                    Re
                    <span className="source-flip textActive text-h1-sm text-gradle-blue md:text-headline-md lg:text-splash-md">
                      Actively
                    </span>
                  </span>
                  <div className="inline-flex text-gradle-blue ">
                    <p className="textActiveTarget inline-flex w-[166px] md:w-[300px]"> </p>
                  </div>
                </div>
                <div className="text-wrapper">
                  <h2 className="text-h4 mb-1 text-center text-gray-5 lg:text-left">troubleshoot failures</h2>
                  <div className="image-wrapper relative mb-20 mt-10 flex lg:mb-0 lg:mt-0 lg:hidden ">
                    <Image
                      src="/assets/images/active/SCREEN-REACTIVE-FNL.png"
                      alt="reactive 1"
                      width={400}
                      height={250}
                      className="image-1 w-full px-6"
                    />
                    <Image
                      src="/assets/images/active/ReActively Section 02.png"
                      alt="reactive 1"
                      width={279}
                      height={175}
                      className=" image-2 absolute bottom-[-10%] right-[10px]"
                    />
                  </div>
                  <h5 className="mb-6 text-body-lg text-gray-4 md:text-eyebrow-lg lg:text-h5-sm">
                    Use Build Scan® to get detailed build action and environment data and metrics for every build to
                    rapidly determine the problem root cause. Enhance your failure analysis with Failure Analytics
                    dashboards.
                  </h5>
                  <Link
                    href="/develocity/product/build-scan/"
                    className="underline-text-transition-blue group mb-1 !flex !w-fit items-center gap-1"
                  >
                    <span className="text-body">Learn More</span>
                    <ChevronRightIcon className="size-6 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
